import React from 'react';
import { ConfigProvider, Checkbox } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import { AgenticArtifact } from '../AskAsperAgentic.types';
import EnhancedBarChart from '../../../molecules/EnhancedBarChart/EnhancedBarChart';
import EnhancedHorizontalBarChart from '../../../molecules/EnhancedHorizontalBarChart/EnhancedHorizontalBarChart';
import EnhancedLineChart from '../../../molecules/EnhancedLineChart/EnhancedLineChart';
import EnhancedStackedBarChart from '../../../molecules/EnhancedStackedBarChart/EnhancedStackedBarChart';
import DataTable from '../../../molecules/DataTable/DataTable';
import BrandPriceLadder from '../../../molecules/BrandPriceLadder/BrandPriceLadder';
import WaterfallChart from '../../../molecules/WaterfallChart/WaterfallChart';
import { VizData } from '../../../molecules/EnhancedBarChart/EnhancedBarChart.types';
import { VizDataFormat } from '../../../molecules/DataTable/DataTable';
import { BrandPriceLadderVizData } from '../../../molecules/BrandPriceLadder/BrandPriceLadder.types';
import { isVizDataErrorState } from '../../../../utils/transformVizData';
import { renderEChartsHtmlIfPresent } from '../../../shared/EChartsHtmlChart';
import XClose from '../../../atoms/Icon/XClose';
import ArrowRight from '../../../atoms/Icon/ArrowRight';

export interface AgenticArtifactCardProps {
    artifact: AgenticArtifact;
    /** Whether to show checkbox for multi-select mode */
    showCheckbox?: boolean;
    /** Whether the checkbox is checked */
    checked?: boolean;
    /** Callback when checkbox state changes */
    onCheckChange?: (checked: boolean) => void;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * AgenticArtifactCard - Individual artifact card component
 *
 * Displays visualization elements like charts, tables, heatmaps
 * Supports various chart types and custom content
 */
const AgenticArtifactCard: React.FC<AgenticArtifactCardProps> = ({
    artifact,
    showCheckbox = false,
    checked = false,
    onCheckChange,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    const handleCheckboxChange = (e: any) => {
        e.stopPropagation();
        onCheckChange?.(e.target.checked);
    };

    // Render visualization based on type
    const renderVisualization = () => {
        // If custom content is provided, use it
        if (artifact.customContent) {
            return artifact.customContent;
        }

        // If vizData is provided, render appropriate chart
        if (artifact.vizData || artifact.html) {
            if (isVizDataErrorState(artifact.vizData)) return null;
            const htmlChart = renderEChartsHtmlIfPresent(
                { html: artifact.html, vizData: artifact.vizData, render: artifact.render },
                `${dataTestId}-echarts-html`,
            );
            if (htmlChart) return htmlChart;
            const chartType = artifact.chartType || artifact.vizData?.chart_type?.toLowerCase() || 'line';
            const normalizedChartType = String(chartType).toLowerCase();

            if (normalizedChartType === 'waterfall') {
                return (
                    <WaterfallChart
                        config={{ viz_data: artifact.vizData }}
                        showConnectors={false}
                        data-test-id={`${dataTestId}-waterfall-chart`}
                    />
                );
            }

            if (normalizedChartType === 'stackbar') {
                return (
                    <EnhancedStackedBarChart
                        vizData={artifact.vizData as any}
                        onBarClick={() => {}}
                        data-test-id={`${dataTestId}-stackbar-chart`}
                    />
                );
            }

            if (normalizedChartType === 'horizontalbar') {
                return (
                    <EnhancedHorizontalBarChart
                        vizData={artifact.vizData as VizData}
                        onBarClick={() => {}}
                        data-test-id={`${dataTestId}-horizontal-bar-chart`}
                    />
                );
            }

            if (normalizedChartType === 'bar') {
                return (
                    <EnhancedBarChart
                        vizData={artifact.vizData as VizData}
                        onBarClick={() => {}}
                        data-test-id={`${dataTestId}-bar-chart`}
                    />
                );
            }

            if (normalizedChartType === 'line') {
                return (
                    <EnhancedLineChart
                        vizData={artifact.vizData as VizData}
                        onPointClick={() => {}}
                        data-test-id={`${dataTestId}-line-chart`}
                    />
                );
            }

            if (normalizedChartType === 'table') {
                return <DataTable vizData={artifact.vizData as VizDataFormat} data-test-id={`${dataTestId}-table`} />;
            }

            if (normalizedChartType === 'bpl') {
                return (
                    <BrandPriceLadder
                        vizData={artifact.vizData as BrandPriceLadderVizData}
                        data-test-id={`${dataTestId}-bpl`}
                    />
                );
            }
        }

        return null;
    };

    // Parse text with **bold** markers
    const parseBoldText = (text: string) => {
        return text.split(/(\*\*.*?\*\*)/g).map((part, index) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                const boldText = part.slice(2, -2);
                return (
                    <strong key={index} className="font-semibold">
                        {boldText}
                    </strong>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`rounded-lg border shadow-sm p-4 md:p-6 mb-4 ${
                    checked ? 'bg-[#fbfaff] border-[#5f49bc]' : 'bg-white border-[rgba(0,0,0,0.06)]'
                }`}
                data-test={dataTest}
                data-test-id={dataTestId}
                data-artifact-card
                data-artifact-id={artifact.id}
            >
                {/* Header */}
                <div className="flex items-center justify-between mb-4 gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                        {showCheckbox && (
                            <Checkbox
                                checked={checked}
                                onChange={handleCheckboxChange}
                                onClick={(e) => e.stopPropagation()}
                                className="[&_.ant-checkbox-checked_.ant-checkbox-inner]:bg-[#5f49bc] [&_.ant-checkbox-checked_.ant-checkbox-inner]:border-[#5f49bc]"
                            />
                        )}
                        <h3 className="font-['Open_Sans'] font-semibold text-[14px] md:text-[16px] leading-[20px] md:leading-[24px] text-black truncate flex-1">
                            {artifact.title}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2">
                        {artifact.onShare && (
                            <button
                                onClick={artifact.onShare}
                                className="p-1 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors"
                                data-test-id={`${dataTestId}-share`}
                            >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M8 2v6M5 5l3-3 3 3M2 10h12" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                            </button>
                        )}
                        {artifact.onClose && (
                            <button
                                onClick={artifact.onClose}
                                className="p-1 hover:bg-[rgba(0,0,0,0.04)] rounded transition-colors"
                                data-test-id={`${dataTestId}-close`}
                            >
                                <XClose size={16} color="rgba(0,0,0,0.45)" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Insight Box */}
                {artifact.insight && (
                    <div className="bg-[#F5F5F5] rounded-lg px-4 py-3 mb-4 flex items-start gap-2">
                        {artifact.insight.icon && <div className="flex-shrink-0 mt-0.5">{artifact.insight.icon}</div>}
                        <div className="flex-1 font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)]">
                            {parseBoldText(artifact.insight.text)}
                        </div>
                    </div>
                )}

                {/* Visualization */}
                <div className="mb-4">{renderVisualization()}</div>

                {/* Methodology Link */}
                {artifact.methodologyLink && (
                    <div className="mt-4 pt-4 border-t border-[rgba(0,0,0,0.06)]">
                        <button
                            onClick={artifact.methodologyLink.onClick}
                            className="flex items-center gap-2 font-['Open_Sans'] text-[14px] leading-[20px] text-[rgba(0,0,0,0.88)] hover:text-[#7C3AED] transition-colors"
                            data-test-id={`${dataTestId}-methodology-link`}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 2v2M8 12v2M2 8h2M12 8h2" stroke="currentColor" strokeWidth="1.5" />
                            </svg>
                            <span>{artifact.methodologyLink.label}</span>
                            <ArrowRight size={14} color="currentColor" />
                        </button>
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default AgenticArtifactCard;
